import { PaginationFilter } from '@liveauctioneers/caterwaul-components/types/PaginationFilter';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';

const defaultPageSizeOptions: OptionType[] = [24, 48, 72, 96, 120].map((value) => ({
    label: value.toString(),
    value,
}));

export type SortPageSizeFilterProps = {
    readonly className?: string;
    readonly onChange: Function;
    /** SelectOptions of what should be rendered for the page size options */
    readonly pageSizeOptions?: OptionType[];
    /** A PaginationFilter object */
    readonly paginationFilter: PaginationFilter;
    /** Should modify query params */
    readonly shouldModifyQueryParams?: boolean;
    /** SelectOptions of what should be rendered for the sort options */
    readonly sortOptions: OptionType[];
    readonly testid?: string;
};

export const SortPageSizeFilter = ({
    className,
    onChange,
    pageSizeOptions = defaultPageSizeOptions,
    paginationFilter,
    shouldModifyQueryParams,
    sortOptions,
    testid = 'sortPageSizeFilter',
}: SortPageSizeFilterProps) => {
    const modifyQueryParams = useModifyQueryParams();

    const onSortChange = useCallback(
        (option: OptionType) => {
            const { value: sort } = option ?? {};
            if (shouldModifyQueryParams) {
                modifyQueryParams({
                    action: ModifyQueryParamsAction.ADD,
                    query: { page: 1, sort },
                });
            }
            onChange({ page: 1, preventScrollReset: true, sort });
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );

    const onPageSizeChange = useCallback(
        (option: OptionType) => {
            const { value: pageSize } = option ?? {};
            if (shouldModifyQueryParams) {
                modifyQueryParams({
                    action: ModifyQueryParamsAction.ADD,
                    query: { page: 1, pageSize },
                });
            }
            onChange({ page: 1, pageSize, preventScrollReset: true });
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );

    return (
        <div
            className={cn('flex justify-between gap-sm xsMax:flex-col', className)}
            data-testid={testid}
        >
            <Select
                className="min-w-[264px]"
                data-testid="sort-select"
                isInsideLabel
                isSearchable={false}
                labelText={<FormattedMessage id="search_alerts.sortLabel" />}
                onChange={onSortChange}
                options={sortOptions}
                small
                value={sortOptions.find((op) => op.value === paginationFilter.sort)}
            />
            <Select
                className="min-w-[132px] xsMax:mb-sm"
                data-testid="page-size-select"
                isInsideLabel
                isSearchable={false}
                labelText={<FormattedMessage id="search_alerts.viewLabel" />}
                onChange={onPageSizeChange}
                options={pageSizeOptions}
                small
                value={pageSizeOptions.find((op) => op.value === paginationFilter.pageSize)}
            />
        </div>
    );
};

export default React.memo<SortPageSizeFilterProps>(SortPageSizeFilter, isEqual);
